import defineType from '@hid/define/define_type'
import { keys } from '@hid/define/define_keys'
import { DVKFromVK, HIDFromVk, VKFromHID, kkFromVK } from '@hid/define/define_fn'
import {
  KeyMap,
  VK,
  type DefineDevice,
  type KK,
  VirtualMG68ACE,
  VirtualMG68ACE_UpPlus,
  VirtualMG68ACE_W,
  VirtualMG68ACE_W_24g,
  VirtualMars68,
  VirtualMars68Pro,
  VirtualMars68ProWireless,
  VirtualMars68ProWireless_24g,
  VirtualMars68SE,
  VirtualMars68Wireless,
  VirtualMars68Wireless_24g,
  VirtualMer68Lite,
  VirtualMer68SE,
  VirtualMg68,
  VirtualMg68Lite,
  VirtualMg68Max,
  VirtualMg68Max_24g,
  VirtualMg68Pro,
  VirtualMg68SE,
  VirtualND63Max,
  VirtualND68,
} from '@hid/define/define'
import { mu68VKCodes } from '@/profiles/keyboard/keycode/vkeycode'
import { getRoyuanFnActionKeyByKeyMap, normalizeRoyuanFnKeyMap } from './device_function'

type RoyuanFnLayout = Array<Array<VK | undefined>>

const royuanFnSignatureToVk = new Map<string, VK>()
const royuanFnMatrixIndexToKKCache = new Map<DefineDevice, { [index: number]: number }>()

const nd63MaxRoyuanFnLayout: RoyuanFnLayout = [
  [0x1b, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x30, 0xbd, 0xbb, 0x08] as Array<VK | undefined>,
  [0x09, 0x51, 0x57, 0x45, 0x52, 0x54, 0x59, 0x55, 0x49, 0x4f, 0x50, 0xdb, 0xdd, 0xdc] as Array<VK | undefined>,
  [0x14, 0x41, 0x53, 0x44, 0x46, 0x47, 0x48, 0x4a, 0x4b, 0x4c, 0xba, 0xde, undefined, 0x0d] as Array<VK | undefined>,
  [0xa0, undefined, 0x5a, 0x58, 0x43, 0x56, 0x42, 0x4e, 0x4d, 0xbc, 0xbe, 0xbf, 0xa1, 0x26, 0x2e] as Array<VK | undefined>,
  [0xa2, 0x5b, 0xa4, undefined, undefined, undefined, 0x20, undefined, undefined, 0xa5, 0xfa, 0xa3, 0x25, 0x28, 0x27] as Array<VK | undefined>,
]

const royuanFnLayoutsByDevice = new Map<DefineDevice, RoyuanFnLayout>([
  [VirtualND68, mu68VKCodes as RoyuanFnLayout],
  [VirtualND63Max, nd63MaxRoyuanFnLayout],
])

const royuan68DefaultFnRawKeyMap = new Map<KK, KeyMap>([
  [0x0001 as KK, [0x00, 0x00, 0x35, 0x00]],
  [0x0009 as KK, [0x0a, 0x05, 0x00, 0x00]],
  [0x000b as KK, [0x0a, 0x03, 0x00, 0x00]],
  [0x0007 as KK, [0x00, 0x00, 0x3a, 0x00]],
  [0x000d as KK, [0x00, 0x00, 0x3b, 0x00]],
  [0x000f as KK, [0x0a, 0x05, 0x01, 0x00]],
  [0x0013 as KK, [0x00, 0x00, 0x3c, 0x00]],
  [0x0016 as KK, [0x0a, 0x0c, 0x00, 0x00]],
  [0x0019 as KK, [0x00, 0x00, 0x3d, 0x00]],
  [0x001f as KK, [0x00, 0x00, 0x3e, 0x00]],
  [0x0025 as KK, [0x00, 0x00, 0x3f, 0x00]],
  [0x002b as KK, [0x00, 0x00, 0x40, 0x00]],
  [0x0031 as KK, [0x00, 0x00, 0x41, 0x00]],
  [0x0037 as KK, [0x00, 0x00, 0x42, 0x00]],
  [0x003d as KK, [0x00, 0x00, 0x43, 0x00]],
  [0x0043 as KK, [0x00, 0x00, 0x44, 0x00]],
  [0x0044 as KK, [0x0d, 0x01, 0x00, 0x00]],
  [0x0049 as KK, [0x00, 0x00, 0x45, 0x00]],
  [0x004a as KK, [0x0d, 0x05, 0x01, 0x00]],
  [0x004d as KK, [0x0d, 0x03, 0x01, 0x00]],
  [0x0052 as KK, [0x0d, 0x02, 0x01, 0x00]],
  [0x0053 as KK, [0x0d, 0x02, 0x02, 0x00]],
  [0x0059 as KK, [0x0d, 0x03, 0x02, 0x00]],
  [0x003b as KK, [0x0a, 0x16, 0x00, 0x00]],
])

const nd63MaxDefaultFnRawKeyMap = new Map<KK, KeyMap>([
  [0x0001 as KK, [0x00, 0x00, 0x35, 0x00]],
  [0x0007 as KK, [0x00, 0x00, 0x3a, 0x00]],
  [0x000b as KK, [0x0a, 0x03, 0x00, 0x00]],
  [0x000d as KK, [0x00, 0x00, 0x3b, 0x00]],
  [0x0013 as KK, [0x00, 0x00, 0x3c, 0x00]],
  [0x0019 as KK, [0x00, 0x00, 0x3d, 0x00]],
  [0x001f as KK, [0x00, 0x00, 0x3e, 0x00]],
  [0x0025 as KK, [0x00, 0x00, 0x3f, 0x00]],
  [0x002b as KK, [0x00, 0x00, 0x40, 0x00]],
  [0x0031 as KK, [0x00, 0x00, 0x41, 0x00]],
  [0x0037 as KK, [0x00, 0x00, 0x42, 0x00]],
  [0x003b as KK, [0x00, 0x00, 0x03, 0x00]],
  [0x003d as KK, [0x00, 0x00, 0x43, 0x00]],
  [0x0043 as KK, [0x00, 0x00, 0x44, 0x00]],
  [0x0044 as KK, [0x0d, 0x01, 0x00, 0x00]],
  [0x0047 as KK, [0x0d, 0x03, 0x01, 0x00]],
  [0x0049 as KK, [0x00, 0x00, 0x45, 0x00]],
  [0x004a as KK, [0x0d, 0x05, 0x01, 0x00]],
  [0x004d as KK, [0x0d, 0x02, 0x02, 0x00]],
  [0x0052 as KK, [0x0d, 0x02, 0x01, 0x00]],
  [0x0053 as KK, [0x0d, 0x03, 0x02, 0x00]],
])

const royuanDefaultFnRawKeyMapByDevice = new Map<DefineDevice, ReadonlyMap<KK, KeyMap>>([[VirtualND63Max, nd63MaxDefaultFnRawKeyMap]])

const royuan68FamilyDevices = new Set<DefineDevice>([
  VirtualMars68,
  VirtualMars68SE,
  VirtualMars68Wireless,
  VirtualMars68Wireless_24g,
  VirtualMars68ProWireless,
  VirtualMars68ProWireless_24g,
  VirtualMars68Pro,
  VirtualMg68,
  VirtualMg68SE,
  VirtualMg68Pro,
  VirtualMg68Max,
  VirtualMg68Max_24g,
  VirtualMg68Lite,
  VirtualMG68ACE,
  VirtualMG68ACE_UpPlus,
  VirtualMG68ACE_W,
  VirtualMG68ACE_W_24g,
  VirtualMer68SE,
  VirtualMer68Lite,
  VirtualND63Max,
  VirtualND68,
])

const royuanFnSixSlotRowMapByDevice = new Map<DefineDevice, number[]>([[VirtualND63Max, [1, 2, 3, 4, 5]]])

const royuanKeySignature = (data: number[]) => normalizeRoyuanFnKeyMap(data).join(',')

const encodeRoyuanBaseKey = (vk: VK): KeyMap | undefined => {
  const setKey = DVKFromVK(vk, VirtualMars68, 'hid')

  const setType = (setKey >> 16) & 0xff
  const setMid = (setKey >> 8) & 0xff
  const hid = setKey & 0xff

  if (defineType.isMedia(vk)) {
    return normalizeRoyuanFnKeyMap([0x03, 0, hid, setMid])
  }

  if (defineType.isMouse(vk)) {
    return normalizeRoyuanFnKeyMap([0x01, 0, setKey, setMid])
  }

  return normalizeRoyuanFnKeyMap([setType, 0, setKey, 0])
}

const getRoyuanFnSignatureToVk = (): ReadonlyMap<string, VK> => {
  if (royuanFnSignatureToVk.size > 0) {
    return royuanFnSignatureToVk
  }

  keys.forEach((key) => {
    try {
      const encoded = encodeRoyuanBaseKey(key.VirtualKey)
      if (encoded !== undefined) {
        royuanFnSignatureToVk.set(royuanKeySignature(encoded), key.VirtualKey)
      }
    } catch (error) {
      void error
    }
  })

  return royuanFnSignatureToVk
}

const decodeRoyuanStandardKey = (normalized: KeyMap): VK | undefined => {
  if (normalized[0] !== 0) {
    return undefined
  }

  const hidCandidates = normalized.slice(1).filter((value) => value !== 0)
  if (hidCandidates.length !== 1) {
    return undefined
  }

  return VKFromHID(hidCandidates[0])
}

const convertRoyuanFnIndexToSixSlotMatrix = (matrixIndexToKK: { [index: number]: number }, rowCount: number, rowSlotMap: number[]) => {
  const indexToKK: { [index: number]: number } = {}

  Object.entries(matrixIndexToKK).forEach(([rawIndex, kk]) => {
    const matrixIndex = Number(rawIndex)
    const horizontalSlot = Math.floor(matrixIndex / rowCount)
    const physicalRow = matrixIndex % rowCount
    const verticalSlot = rowSlotMap[physicalRow] ?? physicalRow

    indexToKK[horizontalSlot * 6 + verticalSlot] = kk
  })

  return indexToKK
}

const buildRoyuanFnMatrixIndexToKKMap = (deviceID: DefineDevice, vkcodes: RoyuanFnLayout): { [index: number]: number } => {
  const matrixIndexToKK: { [index: number]: number } = {}
  const maxColumnCount = Math.max(...vkcodes.map((row) => row.length), 0)
  const rowCount = vkcodes.length

  for (let columnIndex = 0; columnIndex < maxColumnCount; columnIndex++) {
    for (let physicalRow = 0; physicalRow < rowCount; physicalRow++) {
      const vk = vkcodes[physicalRow]?.[columnIndex]
      if (vk === undefined) {
        continue
      }

      const kk = kkFromVK(vk, deviceID)
      if (kk !== undefined) {
        matrixIndexToKK[columnIndex * rowCount + physicalRow] = kk as KK
      }
    }
  }

  const sixSlotRowMap = royuanFnSixSlotRowMapByDevice.get(deviceID)
  if (sixSlotRowMap !== undefined) {
    return convertRoyuanFnIndexToSixSlotMatrix(matrixIndexToKK, vkcodes.length, sixSlotRowMap)
  }

  return matrixIndexToKK
}

export const getRoyuanFnMatrixIndexToKKMap = (deviceID: DefineDevice): { [index: number]: number } => {
  const cached = royuanFnMatrixIndexToKKCache.get(deviceID)
  if (cached !== undefined) {
    return cached
  }

  const vkcodes = royuanFnLayoutsByDevice.get(deviceID) ?? (mu68VKCodes as RoyuanFnLayout)
  const matrixIndexToKK = buildRoyuanFnMatrixIndexToKKMap(deviceID, vkcodes)
  royuanFnMatrixIndexToKKCache.set(deviceID, matrixIndexToKK)
  return matrixIndexToKK
}

export const hasRoyuanDefaultFnLayout = (deviceID: DefineDevice): boolean => {
  return royuan68FamilyDevices.has(deviceID)
}

export const getRoyuanDefaultFnRawKeyMap = (kk: KK, deviceID: DefineDevice): KeyMap | undefined => {
  if (!hasRoyuanDefaultFnLayout(deviceID)) {
    return undefined
  }

  const deviceKeyMap = royuanDefaultFnRawKeyMapByDevice.get(deviceID)
  const keyMap = deviceKeyMap?.get(kk) ?? royuan68DefaultFnRawKeyMap.get(kk)
  if (keyMap === undefined) {
    return [0x00, 0x00, 0x00, 0x00]
  }

  return [...keyMap]
}

export const decodeRoyuanFnKey = (rawKeyData: number[], deviceID: DefineDevice): VK | undefined => {
  const normalized = normalizeRoyuanFnKeyMap(rawKeyData)
  if (normalized.every((value) => value === 0)) {
    return undefined
  }

  const actionKey = getRoyuanFnActionKeyByKeyMap(normalized, deviceID)
  if (actionKey !== undefined) {
    return actionKey as VK
  }

  const mappedVk = getRoyuanFnSignatureToVk().get(royuanKeySignature(normalized))
  if (mappedVk !== undefined) {
    return mappedVk
  }

  const standardKey = decodeRoyuanStandardKey(normalized)
  if (standardKey !== undefined) {
    return standardKey
  }

  return undefined
}
